import { useEffect, useState, type ReactElement } from 'react';
import { practiceWasmGlueUrl } from '../../../app/rounds/practice-wasm';
import { MermaidRuntime } from '../diagram/MermaidRuntime';
import './BppProgramDiagram.css';

export const BPP_MERMAID_THEME = {
  primaryColor: '#FEE977',
  primaryTextColor: '#000000',
  edgeLabelBackground: 'transparent',
} as const;

export type BppDiagramResult =
  | { readonly status: 'ok'; readonly diagram: string }
  | { readonly status: 'invalid' };

export type BppDiagramConverter = (source: string) => Promise<BppDiagramResult>;

type BppGlue = Record<string, unknown> & {
  default?: () => Promise<unknown>;
  bpp_program_diagram?: (source: string) => string;
};

export const convertBppProgram: BppDiagramConverter = async (source) => {
  const url = practiceWasmGlueUrl();
  const glue = (await import(/* @vite-ignore */ url)) as BppGlue;
  if (typeof glue.default === 'function') await glue.default();
  if (typeof glue.bpp_program_diagram !== 'function') {
    throw new Error(`${url} is not a waggle-wasm module: bpp_program_diagram is missing`);
  }
  const parsed = JSON.parse(glue.bpp_program_diagram(source)) as unknown;
  if (
    typeof parsed === 'object' &&
    parsed !== null &&
    (parsed as { status?: unknown }).status === 'ok' &&
    typeof (parsed as { diagram?: unknown }).diagram === 'string'
  ) {
    return { status: 'ok', diagram: (parsed as { diagram: string }).diagram };
  }
  if (typeof parsed === 'object' && parsed !== null && (parsed as { status?: unknown }).status === 'invalid') {
    return { status: 'invalid' };
  }
  throw new Error('bpp_program_diagram returned an unknown envelope');
};

export interface BppProgramDiagramProps {
  source: string;
  label: string;
  color?: string;
  /** Reserved for gap G-B; the authoritative diagram source remains Rust-generated. */
  nodeTelemetry?: Readonly<Record<string, number>>;
  convert?: BppDiagramConverter;
}

/** Mermaid's color parser accepts concrete CSS colors, not custom-property expressions. Resolve
 * the design-system token at the component boundary while the browser's computed theme is in
 * scope; the SVG still receives the exact active token value. */
export function mermaidThemeColor(color: string): string {
  const variable = /^var\((--[^,)]+)(?:,\s*([^)]+))?\)$/.exec(color.trim());
  if (variable === null || typeof document === 'undefined') return color;
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(variable[1]!).trim();
  return resolved || variable[2]?.trim() || '#3264c8';
}

export function BppProgramDiagram({
  source,
  label,
  color = '#3264c8',
  convert = convertBppProgram,
}: BppProgramDiagramProps): ReactElement {
  const [diagram, setDiagram] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDiagram(null);
    setFailed(false);
    void convert(source)
      .then((result) => {
        if (cancelled) return;
        if (result.status === 'ok') setDiagram(result.diagram);
        else setFailed(true);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Unable to generate B++ program diagram', error);
        setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [convert, source]);

  if (failed) return <div aria-label={label}>Diagram unavailable</div>;
  if (diagram === null) return <div aria-label={label}>Preparing diagram…</div>;
  const themeColor = mermaidThemeColor(color);
  return (
    <MermaidRuntime
      source={diagram}
      ariaLabel={label}
      className="bpp-program-diagram"
      themeVariables={{ ...BPP_MERMAID_THEME, lineColor: themeColor }}
    />
  );
}
